// DS3.5.1
import React, { useContext } from 'react';
import c from 'classnames';
import InputErrorMessage from '../../../formControls/lego/InputErrorMessage';
import InputLabelText from '../../../formControls/lego/InputLabelText';
import InputLabelSubText from '../../../formControls/lego/InputLabelSubText';
import FormElement from '../../../formControls/layouts/FormElement';
import ArrowDown from '../../../Icons/16x16/ArrowDown';
import ArrowUp from '../../../Icons/16x16/ArrowUp';
import { IconSize } from '../../../Icons/IconWrap';
import DropdownStateManager, { DropdownStateContext } from '../../DropdownStateManager';
import LoadingIndicator from '../../../LoadingIndicator';
import MultiselectList, { Props as MultiselectListProps } from '../MultiselectList';
import s from './MultiselectField.scss';

export interface Props {
  /** Required for a11y reasons. We need to be able to link auto-complete menus and labels to the input field. */
  id: string;
  className?: string;
  listClassName?: string;
  label: string;
  subLabel?: string;
  isLabelHidden?: boolean;
  placeholder?: string;
  error?: string;
  items: MultiselectListProps['items'];
  innerRef?: React.Ref<HTMLDivElement>;
  onChange?: (isSelected: boolean, value: string) => void;
  onOpen?: () => void;
  isLoading?: boolean;
  withFloatingList?: boolean;
  ignoreClickOutside?: boolean;
  variant?: 'checkbox' | 'radio';
}

const MultiselectField: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  className,
  listClassName,
  label,
  subLabel,
  isLabelHidden,
  placeholder,
  items,
  error,
  innerRef,
  onChange,
  isLoading,
  withFloatingList,
  ignoreClickOutside,
  variant = 'checkbox',
}) => {
  const multiselectListId = `${id}-list`;
  const { isOpen, setToggleRef } = useContext(DropdownStateContext);

  const value =
    variant === 'radio'
      ? items.filter(item => item.selected).map(item => item.label)
      : items
          .filter(item => item.selected)
          .map(item => item.label)
          .join(', ');

  const Icon = isOpen ? ArrowUp : ArrowDown;

  const renderInput = () => (
    <div
      id={id}
      tabIndex={0}
      className={c(s['input'], 'ignore-react-onclickoutside')}
      ref={setToggleRef}
      role="combobox"
      aria-expanded={isOpen ? 'true' : 'false'}
      aria-haspopup="listbox"
      aria-controls={multiselectListId}
    >
      {value && <div className={s['value']}>{value}</div>}
      {!value && placeholder && <div className={s['placeholder']}>{placeholder}</div>}
      <Icon className={s['icon']} size={IconSize.SMALLER} disableDefaultColors />
    </div>
  );

  return (
    <div
      className={c(
        s['root'],
        isOpen && s['open'],
        error && s['error'],
        withFloatingList && s['with-floating-list'],
        className
      )}
    >
      <FormElement
        inputId={id}
        label={<InputLabelText className="label">{label}</InputLabelText>}
        subLabel={
          subLabel && <InputLabelSubText className="sublabel">{subLabel}</InputLabelSubText>
        }
        isLabelHidden={isLabelHidden}
        noBottomSpacing={true}
        details={error && <InputErrorMessage id={`${id}-error`}>{error}</InputErrorMessage>}
        ref={innerRef}
      >
        {renderInput()}
      </FormElement>
      {isOpen && !isLoading && (
        <MultiselectList
          id={multiselectListId}
          items={items}
          className={c(s['list-card'], listClassName)}
          ariaLabel={label}
          onChange={onChange}
          variant={variant}
        />
      )}
      {isOpen && isLoading && (
        <div className={s['list-card']}>
          <LoadingIndicator className={s['loading-indicator']} view="llama" />
        </div>
      )}
    </div>
  );
};
MultiselectField.displayName = 'MultiselectField';

const MultiselectFieldWithDropdownState: React.FC<React.PropsWithChildren<Props>> = props => (
  <DropdownStateManager
    closeOnLeave={false}
    ignoreClickOutside={props.ignoreClickOutside}
    onOpen={props.onOpen}
  >
    <MultiselectField {...props} />
  </DropdownStateManager>
);

export default MultiselectFieldWithDropdownState;
